import React, { useState, useEffect } from "react";
import { get } from "../api/config"; // ✅ use your existing GET function

const ProjectCarousel = () => {
  const [projects, setProjects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  const visibleProjects = 4;

  // ✅ Fetch both ongoing and completed projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const [ongoingRes, completedRes] = await Promise.all([
          get("public/projects?status_id=2"),
          get("public/projects?status_id=3"),
        ]);

        const ongoing = Array.isArray(ongoingRes.data) ? ongoingRes.data : [];
        const completed = Array.isArray(completedRes.data)
          ? completedRes.data
          : [];

        const mixed = [...ongoing, ...completed];
        setProjects(mixed);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // ✅ Auto-slide logic
  useEffect(() => {
    if (!isAutoPlaying || projects.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % projects.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, projects.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    setIsAutoPlaying(false);
  };

  const getVisibleProjects = () => {
    const visible = [];
    for (let i = 0; i < visibleProjects; i++) {
      visible.push(projects[(currentIndex + i) % projects.length]);
    }
    return visible;
  };

  const getProjectImage = (project) => {
    const name = project.project_name?.toLowerCase() || "";
    if (name.includes("drain")) {
      return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=600&h=600&fit=crop";
    } else if (name.includes("road")) {
      return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=600&fit=crop";
    } else {
      return "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=600&fit=crop";
    }
  };

  if (loading) {
    return (
      <div className="w-full py-24 text-center text-gray-600">
        Loading projects...
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="w-full py-24 text-center text-gray-600">
        No projects found.
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 to-gray-100 py-20 px-4 overflow-hidden">
      {/* Title */}
      <div className="max-w-7xl mx-auto mb-16">
        <h2 className="text-5xl font-bold text-gray-900 mb-2">Projects</h2>
        <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
      </div>

      {/* Carousel Container */}
      <div className="relative max-w-7xl mx-auto">
        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-2xl rounded-full p-4 transition-all duration-300 hover:scale-110 group"
          aria-label="Previous"
        >
          <svg
            className="w-8 h-8 text-gray-800 group-hover:text-orange-600 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-2xl rounded-full p-4 transition-all duration-300 hover:scale-110 group"
          aria-label="Next"
        >
          <svg
            className="w-8 h-8 text-gray-800 group-hover:text-orange-600 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-16">
          {getVisibleProjects().map((project, index) => (
            <div
              key={`${project.project_id}-${index}`}
              className="group relative animate-fadeInScale"
              style={{
                animationDelay: `${index * 0.1}s`,
              }}
            >
              <div className="relative aspect-square overflow-hidden rounded-full shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-3xl">
                {/* Project Image */}
                <img
                  src={getProjectImage(project)}
                  alt={project.project_name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Hover Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                  <h3 className="text-white text-xl font-bold text-center mb-2">
                    {project.project_name}
                  </h3>
                  <p className="text-white/90 text-sm text-center">
                    {project.location || "No location info"}
                  </p>
                  <button className="mt-4 bg-white text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105">
                    View Details
                  </button>
                </div>

                {/* Decorative Ring */}
                <div className="absolute inset-0 rounded-full border-4 border-white/20 group-hover:border-orange-500/50 transition-all duration-500"></div>
              </div>

              {/* Project Number */}
              <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300 z-10">
                <span className="text-white font-bold text-lg">
                  {(currentIndex + index) % projects.length + 1}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-3 mt-12">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsAutoPlaying(false);
              }}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? "w-12 h-3 bg-gradient-to-r from-orange-500 to-yellow-500"
                  : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out forwards;
        }
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
        }
      `}</style>
    </div>
  );
};

export default ProjectCarousel;
